/**
 * The D2C paywall treatment: heading, the age warning for clients we cannot quote, a "why go deeper"
 * column and the offer card (press logo, testimonial, purchase button and price line).
 *
 * Presentational only. The legacy onboarding paywall passes its lifetime-plan copy; the consumer
 * flow's self-funding paywall passes the Checkout Session amount and 365-day wording, an expired-access
 * notice when needed, and its own list of what is included.
 */
import type { ReactNode } from 'react';
import { Star, Info } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle, Button, Card } from '@design-system';
import { QUOTE_AGE_RANGE } from '@shared';

interface PaywallAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

interface D2cPaywallOfferProps {
  /** Hosts with their own page heading (the consumer flow's step frame) leave this one out. */
  hideHeading?: boolean;
  isAgeIneligible: boolean;
  /** Shown under the heading (e.g. that self-funding access has ended). */
  notice?: ReactNode;
  valuePropsTitle: string;
  valueProps: ReactNode;
  /** Promotional strip at the top of the offer card. */
  banner?: ReactNode;
  primaryAction: PaywallAction;
  secondaryAction?: PaywallAction;
  priceLine: ReactNode;
}

export function D2cPaywallOffer({
  hideHeading = false,
  isAgeIneligible,
  notice,
  valuePropsTitle,
  valueProps,
  banner,
  primaryAction,
  secondaryAction,
  priceLine,
}: D2cPaywallOfferProps) {
  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col items-center py-6 md:pb-16">
      {!hideHeading && (
        <>
          <h1 className="text-balance text-center text-2xl font-bold text-gray-950 lg:text-4xl">
            Turn your predictions into a real long-term care plan
          </h1>
          <p className="text-foreground mt-3 text-balance text-center text-base lg:text-lg">
            See what your care will likely cost, build a realistic care plan, and compare ways to
            fund it.
          </p>
        </>
      )}

      {isAgeIneligible && (
        <Alert
          variant="warning"
          className="mt-6"
        >
          <Info />
          <AlertTitle>Insurance quotes aren&apos;t available for your age</AlertTitle>
          <AlertDescription>
            Our system currently offer policies to people ages {QUOTE_AGE_RANGE.min}-
            {QUOTE_AGE_RANGE.max}, so we won&apos;t be able to show you policy quotes to compare or
            purchase. You can still use Waterlily to build a self-funding plan, modeling your future
            care costs against your own assets and income, but you won&apos;t be able to shop for
            insurance through us. Continue only if a self-funding plan is what you&apos;re looking
            for.
          </AlertDescription>
        </Alert>
      )}

      {notice && <div className="mt-6 w-full">{notice}</div>}

      <div className="grid-col-1 mt-8 grid w-full gap-8 lg:grid-cols-2">
        <div className="row-start-2 lg:row-start-1">
          <h2 className="text-xl font-bold text-gray-950 md:text-2xl">{valuePropsTitle}</h2>
          <div className="mt-6">{valueProps}</div>
        </div>

        <Card className="row-start-1 flex flex-col gap-4 p-6">
          {banner}

          <div className="flex items-center gap-2">
            <p className="text-xs text-gray-500">As featured in</p>
            <img
              src="/logos/nytimes.svg"
              alt="The New York Times"
              className="h-8"
            />
          </div>

          <h3 className="text-xl font-bold text-gray-950">Finish your LTC plan</h3>

          <div className="bg-muted rounded-lg p-4">
            <div className="flex gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star
                  key={i}
                  className="size-4 fill-amber-400 text-amber-400"
                />
              ))}
            </div>
            <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-gray-700">
              Trusted by thousands of families and financial professionals
            </p>
            <p className="text-foreground mt-3 text-sm italic leading-relaxed">
              &ldquo;We highly recommend their services for planning your long term health care
              needs. Thank you Lily and Waterlily staff for your time and sincere commitment in
              helping families like ours.&rdquo;
            </p>
            <p className="mt-2 text-sm font-medium text-gray-950">-- George Tabora</p>
          </div>

          <Button
            size="lg"
            className="mt-2 w-full rounded-full"
            onClick={primaryAction.onClick}
            disabled={primaryAction.disabled}
          >
            {primaryAction.label}
          </Button>
          {secondaryAction && (
            <Button
              size="lg"
              variant="outline"
              className="w-full rounded-full border-gray-400"
              onClick={secondaryAction.onClick}
              disabled={secondaryAction.disabled}
            >
              {secondaryAction.label}
            </Button>
          )}
          <div className="text-foreground text-center text-xs">{priceLine}</div>
        </Card>
      </div>
    </div>
  );
}
